<script setup lang="ts">
  import { computed, ref } from 'vue';
  import { VueFinalModal } from 'vue-final-modal';
  import type { GeneratedRecordStage } from '../model';
  import DangerIcon from '@/shared/assets/icons/DangerIcon.svg';
  import { IconImage, ModalActions, SelectInput, UiModalHeader } from '@/shared/ui';

  const props = defineProps<{
    stages: GeneratedRecordStage[];
    submitting: boolean;
  }>();

  const emit = defineEmits<{
    (e: 'close'): void;
    (e: 'confirm', stageId: number): void;
  }>();

  const NO_STAGES_MESSAGE = 'Nenhuma etapa cadastrada para esta filial.';

  const selected = ref<number | null>(null);

  const options = computed(() => props.stages.map((stage) => ({ id: stage.id, name: stage.name })));
  const confirmDisabled = computed(() => selected.value === null || props.submitting);

  function onSelect(value: string | number): void {
    const id = value === '' ? Number.NaN : Number(value);
    selected.value = Number.isInteger(id) ? id : null;
  }

  function requestClose(): void {
    if (props.submitting) return;
    emit('close');
  }

  // The disabled button is the first guard against a double click; the emit guard is the second,
  // so a second confirm while a request is in flight never reaches the caller.
  function requestConfirm(): void {
    if (props.submitting || selected.value === null) return;
    emit('confirm', selected.value);
  }
</script>

<template>
  <VueFinalModal
    :model-value="true"
    class="flex items-center justify-center"
    content-class="flex w-full max-w-md flex-col rounded-2xl bg-white-01 shadow-lg"
    overlay-transition="vfm-fade"
    content-transition="vfm-fade"
    :click-to-close="!props.submitting"
    :esc-to-close="!props.submitting"
    @update:model-value="!$event && requestClose()"
  >
    <div data-testid="record-stage-modal">
      <UiModalHeader
        class="border-b border-gray-02 px-6 py-4 text-sm"
        title="Etapa do Registro"
        @close="requestClose"
      />
      <div class="flex flex-col px-6 pb-6 pt-8">
        <IconImage
          :src="DangerIcon"
          alt="Atenção"
          class="mx-auto mb-6 h-6 w-6 text-gray-07"
        />
        <p class="mb-6 text-center text-base text-gray-07">
          Selecione uma etapa para o Registro
        </p>
        <label
          id="record-stage-label"
          class="mb-2 text-sm font-semibold text-gray-07"
        >
          Etapa:
        </label>
        <p
          v-if="options.length === 0"
          class="rounded-lg border border-gray-02 px-4 py-2.5 text-sm text-gray-03"
          data-testid="record-stage-empty"
        >
          {{ NO_STAGES_MESSAGE }}
        </p>
        <SelectInput
          v-else
          :model-value="selected ?? ''"
          :options="options"
          label="Selecione a etapa"
          :auto-select-first="false"
          use-teleport
          aria-labelledby="record-stage-label"
          :disabled="props.submitting"
          @update:model-value="onSelect"
        />
        <ModalActions
          class="mt-8 justify-end"
          :confirm-disabled="confirmDisabled"
          :confirm-loading="props.submitting"
          @close="requestClose"
          @confirm="requestConfirm"
        />
      </div>
    </div>
  </VueFinalModal>
</template>
